import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
    Button,
    Flex,
    InputWrapper,
    Modal,
    NumberInput,
    Select,
    Stack,
    Table,
    Text,
    TextInput,
    Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { inject, observer } from 'mobx-react';
import { IconPill, IconPlus, IconSettings } from '@tabler/icons-react';
import { t } from 'i18next';
import { useGetDefaultSimulation } from '../../../global/entities/Simulation/queries/useGetDefaultSimulation.tsx.tsx';
import { Th, useTable } from '../../../global/hooks/useTable.tsx';
import { ISimulationDrug, ISimulationPayload } from '../../../global/entities/Simulation/Simulation.ts';
import DosageSimulationTableRow from './DosageSimulationTableRow.tsx';
import { useSimulation } from '../../../global/hooks/useSimulation.tsx';
import { usePostSimulationMutation } from '../../../global/entities/Simulation/mutations/usePostSimulationMutation.tsx';
import SimulationSettingModal from '../Modal/SimulationSettingModal.tsx';
import { usePostSimulationRunMutation } from '../../../global/entities/Simulation/mutations/usePostSimulationRunMutation.tsx';
import { useGetSimulationById } from '../../../global/entities/Simulation/queries/useGetSimulationById.tsx';
import { theme } from '../../../global/style/theme.tsx';
import { Placeholder } from '../../../global/component/molecules/Placeholder.tsx';
import { usePutSimulationMutations } from '../../../global/entities/Simulation/mutations/usePutSimulationMutations.tsx';
import { useDeleteSimulation } from '../../../global/entities/Simulation/mutations/useDeleteSimulationMutations.tsx';
import { modals } from '@mantine/modals';
import { PatientStore } from '../../../global/Store/PatientStore.ts';

export type IDosageSimulationTableProps = {
    patientStore?: PatientStore;
};

const DosageSimulationTable: React.FC<IDosageSimulationTableProps> = inject('patientStore')(
    observer(({ patientStore }) => {
        const {
            setErrors,
            weight,
            setWeight,
            therapeuticParams,
            activities,
            advancedSettings,
            setSimulationCurveGraph,
            setSimulationSettingModal,
            errors,
            weightError,
            setWeightError,
            setName,
            name,
            setRowsError,
            rowsError,
            simulationId,
            setSimulationId,
        } = useSimulation();

        const [opened, { open, close }] = useDisclosure(false);
        const [isSubmited, setIsSubmited] = useState<boolean>(false);
        const [nameError, setNameError] = useState<boolean>(false);
        const { data: defaultSimulation, refetch: refetchDefaultSimulation } = useGetDefaultSimulation(
            patientStore?.patientId,
        );
        const postSimulationsMutation = usePostSimulationMutation();
        const putSimulationsMutation = usePutSimulationMutations();
        const postSimulationRunMutation = usePostSimulationRunMutation();
        const { mutateAsync: deleteSimulation } = useDeleteSimulation();
        const { sortedData, setSortedData, sortBy, reverseSortDirection, setSorting } = useTable();
        const { data: simulation, refetch: refetchSimulation } = useGetSimulationById(simulationId?.toString());
        const [pendingExecution, setPendingExecution] = useState(false);

        useEffect(() => {
            if (pendingExecution) {
                handleExecute();
                setPendingExecution(false);
            }
        }, [weight, sortedData, name, pendingExecution]);

        const lastId = useMemo(() => {
            return defaultSimulation?.savedSimulations.length
                ? defaultSimulation?.savedSimulations[defaultSimulation?.savedSimulations.length - 1].id
                : -1;
        }, [defaultSimulation]);

        useEffect(() => {
            setSimulationId(lastId);
        }, [lastId]);

        useEffect(() => {
            if (simulation) {
                const hasWeightChanged = simulation.weight !== weight;
                const hasDrugsChanged = JSON.stringify(simulation.simulationDrugs) !== JSON.stringify(sortedData);
                const hasNameChanged = simulation.name !== name;

                // On applique les changements si besoin
                if (hasWeightChanged) setWeight(simulation.weight);
                if (hasDrugsChanged) setSortedData(simulation.simulationDrugs);
                if (hasNameChanged) setName(simulation.name);

                setSimulationSettingModal(simulation);

                // On garde un flag pour savoir s’il faut exécuter ensuite
                if (hasWeightChanged || hasDrugsChanged || hasNameChanged) 
                    setPendingExecution(true);
                
            } else if (defaultSimulation) {
                setSortedData([]);
                setWeight(defaultSimulation.weight || 0);
                setSimulationSettingModal(defaultSimulation);
                setName('');
            }
        }, [simulation, defaultSimulation]);

        useEffect(() => {
            if (simulationId > 0) 
                refetchSimulation();
            
        }, [simulationId]);

        useEffect(() => {
            if (weight > 0) 
                setWeightError(false);
            
        }, [weight, simulationId]);

        const validateWeight = () => {
            setWeightError(weight === 0);
            return weight > 0;
        };

        const validateName = () => {
            setNameError(!name);
            return !!name;
        };

        const validateRows = () => {
            setRowsError(sortedData && sortedData.length === 0);
            return sortedData.length > 0;
        };

        const validateFieldsRow = () => {
            let isValid = true;
            const newErrors: {
                drugId?: string;
                drug?: string;
                dose?: string;
                quantity?: string;
                time?: string;
            }[] = sortedData
                .map((drug) => {
                    const errors: Partial<Record<'drugId' | 'drug' | 'dose' | 'quantity' | 'time', string>> = {};
                    Object.entries({
                        drug: Number(drug.drugID) <= 0,
                        dose: !drug.drugDoseID,
                        quantity: !drug.drugQuantityID,
                        time: drug.time == '',
                    }).forEach(([key, condition]) => {
                        if (condition) {
                            switch (key) {
                                case 'drug':
                                    errors.drug = t('formError.drugIsMissing');
                                    break;
                                case 'dose':
                                    errors.dose = t('formError.doseIsMissing');
                                    break;
                                case 'quantity':
                                    errors.quantity = t('formError.quantityIsMissing');
                                    break;
                                case 'time':
                                    errors.time = t('formError.timeIsMissing');
                                    break;
                            }
                            isValid = false;
                        }
                    });
                    return Object.keys(errors).length ? { drugId: drug.drugID?.toString() ?? '0', ...errors } : null;
                })
                .filter(
                    (
                        error,
                    ): error is {
                        drugId: string;
                        drug?: string;
                        dose?: string;
                        quantity?: string;
                        time?: string;
                    } => Boolean(error),
                );
            setErrors(newErrors);
            return isValid;
        };

        const addNewSimulationDrug = () => {
            setSortedData((prev) => {
                const minDrugID = Math.min(...prev.map((d) => d.drugID), 0);
                return [
                    ...prev,
                    {
                        uniqueId: crypto.randomUUID(),
                        drugID: minDrugID - 1,
                        drugQuantityID: 0,
                        drugDoseID: 0,
                        time: '',
                    } as ISimulationDrug,
                ];
            });
        };

        const handleCancel = () => {
            const lastId = defaultSimulation?.savedSimulations[defaultSimulation.savedSimulations.length - 1]?.id;
            if (lastId) 
                setSimulationId(lastId);
             else setSimulationId(-1);
        };

        const reset = () => {
            setSimulationId(0);
            setSimulationCurveGraph(null);
            setSortedData([]);
            setName('');
        };

        const handleExecute = useCallback(async () => {
            if (validateFieldsRow() && validateWeight() && validateRows() && validateName()) {
                setIsSubmited(true);

                const otherValues = {
                    patientID: Number(patientStore?.patientId),
                    weight,
                    simulationDrugs: sortedData,
                };

                const cleanedActivities = {
                    ...activities,
                    period2Start: activities?.period2Start === '00:00:00' ? null : activities?.period2Start,
                    period2End: activities?.period2End === '00:00:00' ? null : activities?.period2End,
                };

                const values = {
                    ...therapeuticParams,
                    ...cleanedActivities,
                    ...advancedSettings,
                    ...otherValues,
                };

                try {
                    const rep = await postSimulationRunMutation.mutateAsync(values as ISimulationPayload);
                    setSimulationCurveGraph(rep);
                } catch (e) {
                    console.error(e);
                } finally {
                    setIsSubmited(false);
                }
            }
        }, [validateFieldsRow, validateWeight, validateRows, validateName, patientStore?.patientId]);

        const handleSave = async () => {
            if (validateFieldsRow() && validateWeight() && validateRows() && validateName()) {
                setIsSubmited(true);

                const otherValues = {
                    name: name,
                    patientID: Number(patientStore?.patientId),
                    weight: weight,
                    simulationDrugs: sortedData,
                };
                const cleanedActivities = {
                    ...activities,
                    period2Start: activities?.period2Start === '00:00:00' ? null : activities?.period2Start,
                    period2End: activities?.period2End === '00:00:00' ? null : activities?.period2End,
                };

                const values = {
                    ...therapeuticParams,
                    ...cleanedActivities,
                    ...advancedSettings,
                    ...otherValues,
                };
                let newSimulationId: number;
                try {
                    if (simulationId && simulation?.savedSimulations.find((sim) => sim.name === name)) {
                        newSimulationId = await putSimulationsMutation.mutateAsync({
                            entityId: simulationId.toString(),
                            data: values as ISimulationPayload,
                        });
                    } else 
                        newSimulationId = await postSimulationsMutation.mutateAsync(values as ISimulationPayload);
                    
                } catch (e) {
                    console.error(e);
                } finally {
                    await refetchSimulation().then(() => {
                        refetchDefaultSimulation();
                        setSimulationId(newSimulationId);
                        setIsSubmited(false);
                    });
                }
            }
        };

        const handleDelete = () => {
            setIsSubmited(true);

            deleteSimulation(simulationId.toString())
                .then(() => {
                    return refetchDefaultSimulation();
                })
                .catch((error) => {
                    console.error('Erreur lors de la suppression :', error);
                })
                .finally(() => {
                    setIsSubmited(false);
                    const newDefaults = defaultSimulation?.savedSimulations.filter((el) => el.id !== simulationId);
                    setSimulationId(newDefaults?.length ? newDefaults[newDefaults.length - 1].id : -1);
                });
        };

        const openModalDelete = () =>
            modals.openConfirmModal({
                title: t('modal.deleteItemModal.title'),
                children: (
                    <Stack gap={'sm'}>
                        <Text>{t('modal.deleteItemModal.question')}</Text>
                        <Text fw={'bold'}> {t('modal.deleteItemModal.details')}</Text>
                    </Stack>
                ),
                labels: { confirm: t('button.delete'), cancel: t('button.cancel') },
                confirmProps: { color: 'red' },
                cancelProps: { variant: 'outline', color: 'gray' },
                onCancel: () => console.log('Suppression annulée'),
                onConfirm: handleDelete,
                size: 'lg',
                radius: 'xs',
            });

        const simulationsComboBox = useMemo(() => {
            return defaultSimulation?.savedSimulationsCombo;
        }, [defaultSimulation]);

        return (
            <>
                <Flex gap={'sm'} justify="flex-endl" align="normal" w={'100%'}>
                    <Title order={4}>{t('table.dosageSimulation.title')}</Title>
                </Flex>
                <Flex gap={'sm'} justify="flex-start" align="center" mb={'md'} w={'100%'}>
                    {simulationId === 0 ? (
                        <>
                            <InputWrapper error={nameError && t('formError.requiredName')}>
                                <TextInput
                                    value={name}
                                    onChange={(e) => setName(e.target.value)}
                                    placeholder={t('table.dosageSimulation.enterName')}
                                    styles={{ input: { width: 250 } }}
                                />
                            </InputWrapper>
                            <Button variant="outline" color="gray" onClick={handleCancel}>
                                {t('button.cancel')}
                            </Button>
                            <Button
                                disabled={simulationId < 0}
                                ml={'auto'}
                                size="md"
                                variant="subtle"
                                onClick={open}
                                leftSection={<IconSettings />}
                            >
                                {t('global.settings')}
                            </Button>
                        </>
                    ) : (
                        <>
                            <Select
                                styles={{ input: { width: 250 } }}
                                size={'sm'}
                                placeholder={t('table.dosageSimulation.selectSimulation')}
                                data={simulationsComboBox}
                                value={simulationId > 0 ? simulationId?.toString() || null : null}
                                onChange={(value) => setSimulationId(Number(value))}
                            />
                            <Button variant={'light'} disabled={isSubmited} onClick={reset}>
                                {t('global.new')}
                            </Button>
                            <Button
                                disabled={!simulationId || simulationId === -1 || isSubmited}
                                variant="outline"
                                onClick={openModalDelete}
                            >
                                {t('button.delete')}
                            </Button>
                            <Button
                                disabled={simulationId < 0}
                                ml={'auto'}
                                size="md"
                                variant="subtle"
                                onClick={open}
                                leftSection={<IconSettings />}
                            >
                                {t('global.settings')}
                            </Button>
                        </>
                    )}
                </Flex>
                <Modal
                    withCloseButton={false}
                    opened={opened}
                    onClose={close}
                    title={t('formSimulation.title')}
                    size="lg"
                    radius="xs"
                >
                    <SimulationSettingModal close={close} />
                </Modal>

                <Table>
                    <Table.Thead>
                        <Table.Tr>
                            <Th
                                sorted={sortBy === 'drugID'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('drugID', sortedData)}
                            >
                                {t('table.dosageSimulation.medication')}
                            </Th>
                            <Th
                                sorted={sortBy === 'drugDoseID'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('drugDoseID', sortedData)}
                            >
                                {t('table.dosageSimulation.dose')}
                            </Th>
                            <Th
                                sorted={sortBy === 'drugQuantityID'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('drugQuantityID', sortedData)}
                            >
                                {t('table.dosageSimulation.quantity')}
                            </Th>
                            <Th
                                sorted={sortBy === 'time'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('time', sortedData)}
                            >
                                {t('table.dosageSimulation.time')}
                            </Th>
                            <Th
                                sorted={sortBy === 'meal'}
                                reversed={reverseSortDirection}
                                onSort={() => setSorting('meal', sortedData)}
                            >
                                {t('table.dosageSimulation.meal')}
                            </Th>
                        </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                        {sortedData?.map((simulationDrug: ISimulationDrug, index: number) => {
                            return (
                                <DosageSimulationTableRow key={index} simulationDrug={simulationDrug} index={index} />
                            );
                        })}
                    </Table.Tbody>
                </Table>

                {!sortedData.length && simulationId === 0 && (
                    <Placeholder
                        color={rowsError ? 'red' : 'dimmed'}
                        icon={<IconPill />}
                        title={t('table.dosageSimulation.tablePlaceholder')}
                    />
                )}

                <Flex align="center" w={'100%'} gap={'md'}>
                    <InputWrapper error={weightError && t('formError.weightMin')}>
                        <NumberInput
                            label={t('graphSimulation.weight')}
                            labelProps={{ style: { marginRight: theme.spacing?.xs } }}
                            styles={{
                                label: { order: 1 },
                                input: { order: 2, width: 80 },
                                root: { display: 'flex', flexDirection: 'row-reverse', alignItems: 'center' },
                            }}
                            value={weight.toString()}
                            onChange={(e) => setWeight(Number(e))}
                            placeholder="---"
                            min={1}
                        />
                    </InputWrapper>
                    <Button
                        disabled={isSubmited}
                        variant={'default'}
                        ml={'auto'}
                        leftSection={<IconPlus size={16} />}
                        onClick={() => addNewSimulationDrug()}
                    >
                        {t('table.dosageSimulation.addMedication')}
                    </Button>
                </Flex>
                <Flex align="center" w={'100%'} gap={'md'} justify={'flex-start'}>
                    <Button
                        ml={'auto'}
                        variant={'filled'}
                        disabled={!sortedData.length && !!errors}
                        onClick={handleExecute}
                    >
                        {t('table.dosageSimulation.executeSimulation')}
                    </Button>
                    <Button variant={'filled'} loading={isSubmited} onClick={handleSave}>
                        {t('button.save')}
                    </Button>
                </Flex>
            </>
        );
    }),
);

export default DosageSimulationTable;
